import { Bell, LogOut, Menu, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Avatar } from '../components/Avatar';
import { Tooltip } from '../components/Tooltip';
import { Wordmark } from '../components/Wordmark';
import { isNavSectionVisible, NAV_GROUP_LABEL, resolveNavSections } from './sidebarConfig';
import { SidebarNav } from './SidebarNav';
import { SidebarSearch, SidebarSearchResults } from './SidebarSearch';
import { useNavDynamicItems } from './useNavDynamicItems';
import { useSidebarCollapsed } from './useSidebarCollapsed';

export function DashboardLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-white">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <AppBar />
        <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

function Sidebar() {
  const { collapsed, toggle } = useSidebarCollapsed();

  /* The rail's search box. Lives here because the nav region owns what it replaces. */
  const [navQuery, setNavQuery] = useState('');
  const { hasPermission } = useAuth();

  /*
    THE TREE IS RESOLVED ONCE, HERE — the static sections with the runtime leaves (Attrition's
    categories) put in place — and every reader of it is handed this copy: the rail, the flyout
    beneath it, the search. A second resolution anywhere would be a second answer to disagree.
  */
  const dynamicItems = useNavDynamicItems(hasPermission);
  const sections = resolveNavSections(dynamicItems);

  /*
    One button, two homes. Expanded it sits at the far end of the header, opposite the
    logo; collapsed there is no header room left, so it moves to the top of the rail and
    sticks there. Defined once so the two placements cannot drift in label or behaviour.
  */
  const toggleButton = (
    <Tooltip label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
      <button
        type="button"
        id="sidebar-toggle"
        onClick={toggle}
        aria-expanded={!collapsed}
        aria-controls="app-sidebar"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className={[
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-600',
          'transition hover:bg-slate-300 hover:text-slate-800 focus:outline-none',
          'focus-visible:ring-2 focus-visible:ring-brand-300',
        ].join(' ')}
      >
        {/*
          The hamburger in both states. It is one control that toggles one thing, and a glyph
          that changes under the cursor makes the button look like two different controls;
          the tooltip and `aria-label` already say which way it will go.
        */}
        <Menu className="h-4 w-4" aria-hidden="true" />
      </button>
    </Tooltip>
  );

  return (
    /*
      The rail animates its width rather than swapping between two layouts, so collapsing
      reads as one movement. Content inside is swapped, not squeezed: a label half-way
      through a width transition is worse than no label at all.
    */
    <aside
      id="app-sidebar"
      data-collapsed={collapsed}
      className={[
        'flex shrink-0 flex-col border-r border-slate-300 bg-rail',
        'transition-[width] duration-200 ease-out motion-reduce:transition-none',
        collapsed ? 'w-16' : 'w-60',
      ].join(' ')}
    >
      <div
        className={[
          'flex h-16 shrink-0 items-center border-b border-slate-300',
          collapsed ? 'justify-center px-0' : 'gap-2.5 px-4',
        ].join(' ')}
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-500">
          <ShieldCheck className="h-4 w-4 text-white" aria-hidden="true" />
        </div>

        {collapsed ? null : (
          <>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800"><Wordmark /></p>
              <p className="truncate text-[11px] text-slate-500">Integrated systems</p>
            </div>

            {/* Far end of the header, opposite the mark. */}
            <div className="ml-auto -mr-1">{toggleButton}</div>
          </>
        )}
      </div>

      <nav className="flex min-h-0 flex-1 flex-col" aria-label="Main">
        {/*
          THE RAIL'S LEADING CHROME, and it STAYS PUT while the nav scrolls under it.

          The SEARCH leads the rail (product call, 26 Oct 2026) — the first thing in the nav,
          above every suite. Collapsed it is an icon that opens its own panel; expanded it is
          the field, and a query REPLACES the sections rather than filtering them in place.
          Replacing is the honest arrangement: a reader who typed is asking "where is X", and
          an answer buried among thirty unmatched rows is the question unanswered. Clearing the
          box brings the rail straight back, so nothing is lost.

          **It was STICKY INSIDE the scroller and is now ABOVE it** (26 Oct 2026). The `<nav>`
          itself used to scroll, so its 8px scrollbar track ran the full height — including
          down the side of the field, leaving a strip of rail beside a search meant to meet the
          edge. Sticky put the field in the right PLACE and could do nothing about the gutter
          it was sitting next to.

          Lifting it out fixes both at once and DELETES machinery: a header that does not
          scroll needs no `sticky`, no `-top-3`, no negative margins to climb over the nav's
          padding, and no `bg-rail` to hide what passes behind it.
        */}
        <div className="shrink-0">
          {collapsed ? (
            <div className="flex justify-center pb-2 pt-3">{toggleButton}</div>
          ) : null}

          <div className={collapsed ? 'flex justify-center pb-2' : 'flex'}>
            <SidebarSearch sections={sections} value={navQuery} onChange={setNavQuery} collapsed={collapsed} />
          </div>
        </div>

        {/*
          THE ONE SCROLLING REGION, and it carries the padding the nav used to — the sections
          are what scrolls, so the track belongs to them and starts where they start.
        */}
        <div
          className={`modern-scrollbar min-h-0 flex-1 overflow-y-auto py-3 ${
            collapsed ? 'px-3' : 'px-2'
          }`}
        >
        {!collapsed && navQuery.trim() !== '' ? (
          <SidebarSearchResults
            sections={sections}
            query={navQuery}
            inputId="sidebar-search"
            onQueryChange={setNavQuery}
            onNavigate={() => setNavQuery('')}
          />
        ) : (
        <>
        {/*
          Sections whose every entry is gated away are dropped along with their heading. A
          suite label over empty space reads as a nav that failed to load, not as a
          permission the reader lacks.
        */}
        {sections.filter((section) => isNavSectionVisible(section, hasPermission)).map((section) => (
          <div key={section.suite} className="mb-4">
            {/*
              A collapsed rail has no room for the suite label, but the grouping is still
              real — a rule carries it visually and the name stays for screen readers.
            */}
            {collapsed ? (
              <>
                <p className="sr-only">{section.suite}</p>
                <div className="mx-1 mb-2 border-t border-slate-300" role="presentation" />
              </>
            ) : (
              <p className={`px-2 pb-1.5 ${NAV_GROUP_LABEL}`}>
                {section.suite}
              </p>
            )}

            <SidebarNav nodes={section.items} collapsed={collapsed} />
          </div>
        ))}
        </>
        )}
        </div>
      </nav>
    </aside>
  );
}


function AppBar() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // A menu left open across a route change is a small bug users notice immediately.
  useEffect(() => {
    setMenuOpen(false);
    setBellOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen && !bellOpen) return;

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target)) setMenuOpen(false);
      if (!bellRef.current?.contains(target)) setBellOpen(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setBellOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, bellOpen]);

  return (
    <header className="flex h-16 shrink-0 items-center justify-end gap-3 border-b border-slate-200 bg-slate-50 px-4">
      {/* Notifications sit left of the account block. */}
      <div className="relative" ref={bellRef}>
        <Tooltip label="Notifications">
          <button
            type="button"
            id="appbar-notifications"
            onClick={() => setBellOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={bellOpen}
            aria-label="Notifications"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-200 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
          >
            <Bell className="h-4 w-4" aria-hidden="true" />
          </button>
        </Tooltip>

        {bellOpen ? (
          <div
            role="menu"
            className="absolute right-0 top-full z-40 mt-1 w-64 rounded-panel border border-slate-200 bg-white py-1 shadow-raised"
          >
            {/*
              Deliberately says there is nothing rather than implying a feature that does
              not exist yet. Nothing writes notifications today.
            */}
            <p className="px-3 py-4 text-center text-sm text-slate-500">No notifications.</p>
          </div>
        ) : null}
      </div>

      {/* Account: name and role, then the avatar on the trailing edge. */}
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          id="appbar-account-menu"
          onClick={() => setMenuOpen((v) => !v)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
        >
          <span className="hidden min-w-0 text-right sm:block">
            <span className="block truncate text-sm font-medium text-slate-800">
              {user?.displayName}
            </span>
            <span className="block truncate text-xs text-slate-600">{user?.roleName}</span>
          </span>

          <Avatar name={user?.displayName} email={user?.email} size="md" tone="brand" />
        </button>

        {menuOpen ? (
          <div
            role="menu"
            className="absolute right-0 top-full z-40 mt-1 w-56 rounded-panel border border-slate-200 bg-white py-1 shadow-raised"
          >
            <div className="border-b border-slate-100 px-3 py-2">
              <p className="truncate text-sm text-slate-700">{user?.displayName}</p>
              <p className="truncate font-mono tabular-nums text-xs text-slate-500">
                {user?.email}
              </p>
            </div>
            <button
              type="button"
              role="menuitem"
              id="appbar-sign-out"
              onClick={() => void logout()}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Sign out
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
